import { useRouter } from 'next/router'
import { createContext, useEffect, useState } from 'react'
import { getInsuranceProviderRoute } from 'dataSource/insuranceProvider/routes'
import useApi from 'hooks/useApi'

const insuranceProviderInitial = {
  insuranceProvider: null,
  insurancePlan: null,
  insuranceDisplayValue: '',
  setInsuranceData: (
    _insuranceProvider: InsuranceProvider,
    _insurancePlan?: InsurancePlan
  ) => null,
  selfPay: false,
}

export const InsuranceProviderContext = createContext(insuranceProviderInitial)

// This context provider component handles the state for insurance
// Listens to the url router to check insuranceId and gets insurance accordingly
// This component should be wrapped on page level and not on component level
const InsuranceProvider: React.FC = ({ children }) => {
  //---------------------------- states
  const { query } = useRouter()
  const { request } = useApi()
  const [insuranceProvider, setInsuranceProvider] = useState<InsuranceProvider>(
    null
  )
  const [insurancePlan, setInsurancePlan] = useState<InsurancePlan>(null)
  const [insuranceDisplayValue, setInsuranceDisplayValue] = useState<string>('')
  const [selfPay, setSelfPay] = useState<boolean>(false)

  //----------------------------- getInsuranceAPI
  const getInsuranceProviderAPI = async ({ insuranceId, insurancePlan }) => {
    try {
      const insuranceProvider: InsuranceProvider = await request.get(
        getInsuranceProviderRoute(insuranceId)
      )
      const selectedInsurancePlan = insuranceProvider.insurancePlans?.find(
        (plan) => plan.id === insurancePlan
      )

      setInsuranceData(insuranceProvider, selectedInsurancePlan)
    } catch (error) {}
  }

  // If you want to explicitly
  const setInsuranceData = (
    insuranceProvider: InsuranceProvider,
    insurancePlan: InsurancePlan = null
  ) => {
    let insuranceDisplayValue = ''

    if (insuranceProvider) {
      insuranceDisplayValue = insuranceProvider.name
    }

    if (insurancePlan) {
      insuranceDisplayValue = `${insuranceDisplayValue}, ${insurancePlan.name}`
    }

    setInsuranceProvider(insuranceProvider)
    setInsurancePlan(insurancePlan)
    setInsuranceDisplayValue(insuranceDisplayValue)
  }

  useEffect(() => {
    const { insuranceId, insurancePlan, selfPay } = query

    if (selfPay === 'true') {
      setSelfPay(true)
      setInsuranceDisplayValue('Self Pay')
      setInsuranceProvider(null)
      setInsurancePlan(null)
      return
    } else {
      setSelfPay(false)
    }

    if (insuranceId) {
      getInsuranceProviderAPI({ insuranceId, insurancePlan })
    } else {
      setInsuranceProvider(null)
      setInsurancePlan(null)
      setInsuranceDisplayValue(null)
    }
  }, [query.insuranceId, query.insurancePlan, query.selfPay])

  return (
    <InsuranceProviderContext.Provider
      value={{
        insuranceProvider,
        insurancePlan,
        insuranceDisplayValue,
        setInsuranceData,
        selfPay,
      }}
    >
      {children}
    </InsuranceProviderContext.Provider>
  )
}

export default InsuranceProvider
